/**
 * AdaptiveRadiusCircle — renders a radius circle using WebGL (deck.gl ScatterplotLayer)
 * when supported, falling back to a native Google Maps Circle.
 *
 * WebGL renders at zIndex 5 (below markers at zIndex 10).
 * DOM Circle renders at zIndex 1 with the same visual appearance.
 *
 * Intentionally NOT wrapped in React.memo — the DeckOverlay underneath needs
 * re-renders to stay in sync with the Google Maps overlay lifecycle. Memoizing
 * here has been observed to block the circle from rendering until the user
 * interacts with the map.
 */
import PropTypes from 'prop-types'
import { Circle } from 'components/configured-service/Schedule/Circle'
import WebGLServiceRadiusCircle from 'components/configured-service/Schedule/WebGLServiceRadiusCircle'
import { useWebGLSupport } from 'components/common/map/webgl/useWebGLSupport'
import WebGLMarkerSurface from 'components/common/map/webgl/WebGLMarkerSurface'

const AdaptiveRadiusCircle = ({ center, radius, strokeColor, strokeOpacity = 1, strokeWeight = 3, fillColor, fillOpacity = 0.3 }) => {
  const webglSupported = useWebGLSupport()

  if (!center || !radius || radius <= 0) return null

  // Funnel through WebGLMarkerSurface so an async WebGL crash degrades to the
  // DOM Circle via the shared error boundary — the previous bare `if`/`else`
  // had no such safety net.
  const domCircle = (
    <Circle
      zIndex={1}
      center={center}
      radius={radius}
      strokeColor={strokeColor}
      strokeOpacity={strokeOpacity}
      strokeWeight={strokeWeight}
      fillColor={fillColor}
      fillOpacity={fillOpacity}
    />
  )

  return (
    <WebGLMarkerSurface enabled={webglSupported} componentName="AdaptiveRadiusCircle" fallback={domCircle}>
      <WebGLServiceRadiusCircle
        center={center}
        radius={radius}
        strokeColor={strokeColor}
        strokeOpacity={strokeOpacity}
        strokeWeight={strokeWeight}
        fillColor={fillColor}
        fillOpacity={fillOpacity}
      />
    </WebGLMarkerSurface>
  )
}

AdaptiveRadiusCircle.propTypes = {
  center: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
  radius: PropTypes.number,
  strokeColor: PropTypes.string,
  strokeOpacity: PropTypes.number,
  strokeWeight: PropTypes.number,
  fillColor: PropTypes.string,
  fillOpacity: PropTypes.number,
}

export default AdaptiveRadiusCircle
